import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home,
  Car,
  Layers,
  Sparkles,
  Building2,
  MapPin,
  CheckCircle,
  Phone,
  ArrowRight
} from "lucide-react";

const services = [
  {
    id: "house-washing",
    icon: Home,
    title: "House Washing",
    subtitle: "Restore Your Home's Curb Appeal",
    description: "Our professional house washing service removes years of dirt, grime, mold, and mildew from your home's exterior. Using soft washing techniques, we safely clean vinyl, wood, brick, stucco, and aluminum siding without causing damage.",
    benefits: [
      "Increases curb appeal and property value",
      "Removes harmful mold, mildew, and algae",
      "Extends the life of your siding",
      "Safe for all exterior materials",
      "Eco-friendly cleaning solutions"
    ],
    process: [
      "Initial assessment of your home's exterior",
      "Pre-treatment of problem areas",
      "Soft washing with appropriate pressure",
      "Rinse and final inspection",
      "Cleanup of work area"
    ],
    image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?q=80&w=1200"
  },
  {
    id: "driveway-cleaning",
    icon: Car,
    title: "Driveway Cleaning",
    subtitle: "Eliminate Stains and Restore Concrete",
    description: "Transform your stained, dirty driveway into a clean, welcoming entrance. Our high-pressure cleaning removes oil stains, tire marks, rust, and embedded dirt from concrete, asphalt, and paver driveways.",
    benefits: [
      "Removes oil, grease, and rust stains",
      "Eliminates slippery moss and algae",
      "Prevents concrete deterioration",
      "Enhances safety and curb appeal",
      "Prepares surface for sealing"
    ],
    process: [
      "Surface evaluation and stain identification",
      "Pre-treatment of oil and grease stains",
      "High-pressure hot water cleaning",
      "Edge and corner detailing",
      "Post-clean inspection"
    ],
    image: "https://images.unsplash.com/photo-1558036117-15d82a90b9b1?q=80&w=1200"
  },
  {
    id: "deck-patio",
    icon: Layers,
    title: "Deck & Patio Cleaning",
    subtitle: "Revitalize Your Outdoor Living Space",
    description: "Bring your outdoor entertainment areas back to life with our professional deck and patio cleaning service. We clean all types of surfaces including wood, composite, concrete, pavers, and natural stone.",
    benefits: [
      "Restores natural wood color",
      "Removes green algae and black mold",
      "Prepares decks for staining or sealing",
      "Safe for all deck materials",
      "Extends deck and patio lifespan"
    ],
    process: [
      "Assessment of deck material and condition",
      "Application of specialized cleaner",
      "Gentle pressure washing",
      "Board-by-board attention",
      "Final rinse and dry time"
    ],
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1200"
  },
  {
    id: "roof-cleaning",
    icon: Sparkles,
    title: "Roof Cleaning",
    subtitle: "Protect Your Home's First Line of Defense",
    description: "Our soft wash roof cleaning safely removes black streaks, moss, lichen, and algae without damaging your shingles. We use specialized low-pressure techniques approved by roofing manufacturers.",
    benefits: [
      "Removes unsightly black streaks",
      "Prevents premature roof replacement",
      "Improves energy efficiency",
      "No high-pressure damage risk",
      "Manufacturer-approved methods"
    ],
    process: [
      "Roof inspection and safety setup",
      "Application of eco-friendly solution",
      "Soft wash treatment",
      "Gentle rinse of roof surface",
      "Gutter and downspout cleaning"
    ],
    image: "https://images.unsplash.com/photo-1513584684374-8bab748fbf90?q=80&w=1200"
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Pressure Washing",
    subtitle: "Maintain Your Professional Image",
    description: "First impressions matter in business. Our commercial pressure washing services keep your storefront, parking lot, sidewalks, and building exterior looking professional and inviting to customers.",
    benefits: [
      "Enhances professional appearance",
      "Reduces slip and fall liability",
      "Removes graffiti and gum",
      "Flexible scheduling options",
      "Maintenance contracts available"
    ],
    process: [
      "Site assessment and planning",
      "Off-hours scheduling",
      "Comprehensive cleaning execution",
      "Waste water management",
      "Final walk-through with management"
    ],
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200"
  },
  {
    id: "sidewalk",
    icon: MapPin,
    title: "Sidewalk Cleaning",
    subtitle: "Safe and Clean Walkways",
    description: "Keep your sidewalks, walkways, and pathways clean and safe with our professional sidewalk cleaning service. We remove dirt, gum, stains, and slippery substances that can cause accidents.",
    benefits: [
      "Reduces slip and fall hazards",
      "Removes gum and sticky residue",
      "Eliminates unsightly stains",
      "Improves property aesthetics",
      "Regular maintenance plans"
    ],
    process: [
      "Debris and obstacle removal",
      "Pre-treatment of problem areas",
      "High-pressure surface cleaning",
      "Edge and crack detailing",
      "Safety inspection"
    ],
    image: "https://images.unsplash.com/photo-1449157291145-7efd050a4d0e?q=80&w=1200"
  }
];

export default function ServicesPage() {
  const location = useLocation();

  useEffect(() => {
    // Handle hash navigation
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="services-hero">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2070"
            alt="Pressure washing background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-sky-400 font-bold uppercase tracking-widest text-sm mb-4 block"
            >
              Our Services
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="heading-primary text-white mb-6"
            >
              Professional Pressure Washing Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl text-slate-300"
            >
              From residential homes to commercial properties, we provide comprehensive 
              pressure washing solutions tailored to your specific needs.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Services List */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
          data-testid={`service-section-${service.id}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-sky-100 flex items-center justify-center">
                    <service.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <div>
                    <span className="text-sky-600 font-bold uppercase tracking-widest text-sm">
                      {service.title}
                    </span>
                  </div>
                </div>
                
                <h2 className="heading-secondary text-slate-900 mb-4">
                  {service.subtitle}
                </h2>
                
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="font-bold text-slate-900 mb-4 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      Benefits
                    </h4>
                    <ul className="space-y-3">
                      {service.benefits.map((benefit) => (
                        <li key={benefit} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-sky-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-600 text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-slate-900 mb-4 uppercase tracking-wide" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      Our Process
                    </h4>
                    <ol className="space-y-3">
                      {service.process.map((step, i) => (
                        <li key={step} className="flex items-start gap-3">
                          <span className="w-6 h-6 bg-sky-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-slate-600 text-sm">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                
                <Link 
                  to="/contact" 
                  className="btn-primary inline-block"
                  data-testid={`service-cta-${service.id}`}
                >
                  <span>Get Free Quote</span>
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[500px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="cta-gradient py-16" data-testid="services-cta">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="heading-secondary text-white mb-4">
                Need a Custom Solution?
              </h2>
              <p className="text-slate-300 text-lg">
                Contact us for a personalized quote tailored to your specific needs.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary" data-testid="services-quote-btn">
                <span>Request Quote</span>
              </Link>
              <a href="tel:+1-888-435-4674" className="btn-secondary" data-testid="services-call-btn">
                <span className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Call Us Now
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
